import classNames from "classnames";
import { useFormik } from "formik";
import { useEffect } from "react";
import CustomInput from "shared/components/common/customInput";
import CustomSelect from "shared/components/common/customSelect";
import { JobRole } from "shared/utils/enum";
import { QuestionnaireStep1VS } from "shared/utils/validation";
import styles from "./style.module.scss";

interface formikValuesInterface {
  jobs: QuestionnaireStep1Type[];
}

interface QuestionnaireStep1Props {
  formikRef: any;
  formData: any;
  isEdit?: boolean;
  evalsLoading?: boolean;
  initialFormData: any;
}

const QuestionnaireStep1 = ({
  formikRef,
  formData,
  isEdit,
  evalsLoading,
  initialFormData,
}: QuestionnaireStep1Props) => {
  const initialValues: formikValuesInterface = {
    jobs: [
      {
        jobTitle: "",
        department: "",
        jobCode: "",
        jobGrade: "",
        roleType: null,
        rasScore: 0,
        rasLevel: 0,
        shortCode: "",
      },
    ],
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: QuestionnaireStep1VS,
    onSubmit: (value, action) => {
      action.setSubmitting(false);
    },
  });
  const { handleChange, values, touched, errors } = formik;

  const handleInitialSetup = () => {
    let jobs = formData;
    let fields: any = [];
    for (let k = 0; k < jobs?.length; k++) {
      let obj = {
        id: jobs?.[k]?.id ? jobs?.[k]?.id : "0",
        jobTitle: jobs?.[k]?.jobTitle,
        department: jobs?.[k]?.department,
        jobCode: jobs?.[k]?.jobCode,
        jobGrade: jobs?.[k]?.jobGrade,
        roleType: jobs?.[k]?.roleType,
        rasScore: jobs?.[k]?.rasScore ? jobs?.[k]?.rasScore : 0,
        rasLevel: jobs?.[k]?.rasLevel ? jobs?.[k]?.rasLevel : 0,
        shortCode: jobs?.[k]?.shortCode ? jobs?.[k]?.shortCode : "",
      };
      fields.push(obj);
    }
    formik.setFieldValue("jobs", fields);
  };

  useEffect(() => {
    formikRef.current = formik;
    // eslint-disable-next-line
  }, [formik]);

  useEffect(() => {
    if (formData?.length > 0 && !evalsLoading) {
      handleInitialSetup();
    }
    // eslint-disable-next-line
  }, [evalsLoading, formData]);

  return (
    <div className={classNames(styles.stepOneContainer)}>
      <div className={classNames(styles.head)}>
        <h1>Complete Evaluation</h1>
      </div>

      <form
        className={classNames(styles.tableWrapper)}
        onSubmit={(e) => e.preventDefault()}
      >
        <table className="table" style={{ minWidth: "800px", margin: "0px" }}>
          <thead>
            <tr style={{ height: "44px" }}>
              <th className={classNames(styles.tableHead)}>
                Job Title <span>*</span>
              </th>
              <th className={classNames(styles.tableHead)}>
                Department <span>*</span>
              </th>
              <th className={classNames(styles.tableHead)}>Job Code</th>
              <th className={classNames(styles.tableHead)}>Job Grade</th>
              <th className={classNames(styles.tableHead)}>
                Role Type <span>*</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {values.jobs.map((job, index) => {
              return (
                <RowItem
                  job={job}
                  index={index}
                  key={index}
                  formik={formik}
                  touched={touched}
                  errors={errors}
                  handleChange={handleChange}
                  isEdit={isEdit}
                  formData={initialFormData}
                />
              );
            })}
          </tbody>
        </table>
      </form>
    </div>
  );
};

const RowItem = ({
  job,
  touched,
  index,
  handleChange,
  formik,
  errors,
  isEdit,
  formData,
}: any) => {
  const options = [
    {
      title: "Manager",
      Icon: null,
      action: (index: number) => {
        formik.setFieldValue(`jobs[${index}].roleType`, {
          name: "Manager",
          value: JobRole.management,
        });
      },
    },
    {
      title: "Individual Contributor",
      Icon: null,
      action: (index: number) => {
        formik.setFieldValue(`jobs[${index}].roleType`, {
          name: "Individual Contributor",
          value: JobRole.individualContributor,
        });
      },
    },
  ];

  return (
    <tr key={index} className="relative">
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "20%" }}
      >
        <CustomInput
          required
          placeholder="Enter Job Title"
          value={job.jobTitle}
          onChange={handleChange("jobs." + index + ".jobTitle")}
          error={
            touched.jobs?.[index]?.jobTitle &&
            //@ts-ignore
            errors.jobs?.[index]?.jobTitle
          }
          notRequiredMargin
          customInputContainer={classNames(styles.customInputStyle)}
          readOnly={isEdit}
        />
      </td>
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "20%" }}
      >
        <CustomInput
          required
          placeholder="Enter Department"
          value={job.department}
          onChange={handleChange("jobs." + index + ".department")}
          error={
            touched.jobs?.[index]?.department &&
            //@ts-ignore
            errors.jobs?.[index]?.department
          }
          notRequiredMargin
          customInputContainer={classNames(styles.customInputStyle)}
          readOnly={isEdit}
        />
      </td>
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "20%" }}
      >
        <CustomInput
          placeholder="Enter Job Code"
          value={job.jobCode}
          onChange={handleChange("jobs." + index + ".jobCode")}
          error={
            //@ts-ignore
            errors.jobs?.[index]?.jobCode
          }
          notRequiredMargin
          readOnly={isEdit}
          customInputContainer={classNames(styles.customInputStyle)}
        />
      </td>
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "20%" }}
      >
        <CustomInput
          placeholder="Enter Job Grade"
          value={job.jobGrade}
          onChange={handleChange("jobs." + index + ".jobGrade")}
          error={
            touched.jobs?.[index]?.jobGrade &&
            //@ts-ignore
            errors.jobs?.[index]?.jobGrade
          }
          notRequiredMargin
          customInputContainer={classNames(styles.customInputStyle)}
          readOnly={formData[0]?.jobGrade !== "" && isEdit}
        />
      </td>
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "20%" }}
      >
        <div className={classNames("flex items-center gap-2")}>
          <CustomSelect
            placeholder="Select Role Type"
            required
            options={options}
            value={job.roleType?.name}
            readOnly={isEdit}
            error={
              touched.jobs?.[index]?.roleType &&
              //@ts-ignore
              errors.jobs?.[index]?.roleType
            }
            tabIndex={index}
            notRequiredMargin
            customInputContainer={classNames(styles.selectionContainer)}
          />
        </div>
      </td>
    </tr>
  );
};

export default QuestionnaireStep1;
